<template>
  <main-layout
    controlsBg="light"
    :hasControlsBar="false"
    :breadcrumb="getBreadcrumb">
    <div class="container">
      <grid-row column-align="center">
        <grid-col col-size="4">
          <h1 class="header margin-top-xl">
            <chevron
              @click="$router.push({name: 'Settings'})"/>
            {{ $t('health.manageAccess.title') }}
          </h1>
          <alert
            v-if="alertVisible"
            :message="alertMessage"
            :type="alertType">
          </alert>
        </grid-col>
      </grid-row>
      <grid-row class="margin-top-m" column-align="center">
        <grid-col col-size="4">
          <table-component
            ref="accessibleUsersTable"
            :data="fetchAccessiblesData"
            sort-by="username"
            sort-order="asc"
            :show-caption="false"
            class="datatable"
            filter-placeholder=""
            filterNoResults="">
            <table-column
              show="username"
              :label="$t('user.name')"
              header-class="text-left"
              cell-class="table-component__text-left">
            </table-column>
            <table-column
              label=""
              :sortable="false"
              :filterable="false"
              header-class="table-component__minimum__width__column"
              cell-class="text-center">
              <template slot-scope="row">
                <font-awesome-icon
                  v-on:click="onRemoveAccess(row)"
                  :icon="icon.trash"
                />
              </template>
            </table-column>
          </table-component>
        </grid-col>
      </grid-row>
      <grid-row column-align="center">
        <grid-col col-size="4">
          <h2 class="margin-top-xl">
            {{$t('health.manageAccess.invitations')}}
          </h2>
        </grid-col>
      </grid-row>
      <grid-row class="" column-align="center">
        <grid-col col-size="4">
          <table-component
            ref="pendingUsersTable"
            :data="fetchPendingData"
            sort-by="username"
            sort-order="asc"
            :show-caption="false"
            class="datatable"
            filter-placeholder=""
            filterNoResults="">
            <table-column
              show="username"
              :label="$t('user.name')"
              header-class="text-left"
              cell-class="table-component__text-left">
            </table-column>
            <table-column
              label=""
              :sortable="false"
              :filterable="false"
              header-class="table-component__minimum__width__column"
              cell-class="text-center">
              <template slot-scope="row">
                <font-awesome-icon
                  class="pointer"
                  v-on:click="onAcceptAccess(row)"
                  :icon="icon.check"
                />
              </template>
            </table-column>
            <table-column
              label=""
              :sortable="false"
              :filterable="false"
              header-class="table-component__minimum__width__column"
              cell-class="text-center">
              <template slot-scope="row">
                <font-awesome-icon
                  class="pointer rotated"
                  v-on:click="onDeclineAccess(row)"
                  :icon="icon.cross"
                />
              </template>
            </table-column>
          </table-component>
        </grid-col>
      </grid-row>
    </div>
    <ok-cancel-modal
      v-if="showConfirmationDeleteAccessModal"
      v-on:ok="onDeleteAccessConfirm"
      v-on:cancel="onDeleteAccessCancel"
      :title="deleteAccessModalMessage"
      :message="deleteAccessModalTitle"
      :okButton="$t('commons.delete')"
      :cancelButton="$t('commons.cancel')">
      <div slot="body">
        <ul>
          <li>
            {{ accessToRemove.username }}
          </li>
        </ul>
      </div>
    </ok-cancel-modal>
    <ok-cancel-modal
      v-if="showConfirmationPendingCancelModal"
      v-on:ok="onDeclineAccessConfirm"
      v-on:cancel="onDeclineAccessCancel"
      :title="deletePendingCancelModalMessage"
      :message="deletePendingCancelModalTitle"
      :okButton="$t('commons.delete')"
      :cancelButton="$t('commons.cancel')">
      <div slot="body">
        <ul>
          <li>
            {{ accessToDecline.username }}
          </li>
        </ul>
      </div>
    </ok-cancel-modal>
  </main-layout>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import Alert from '@/components/shared/alert/Alert';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import pagination from '@/components/shared/table/pagination';
import Chevron from '@/components/shared/Chevron';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {
  faTrashAlt,
  faCheck
} from '@fortawesome/pro-regular-svg-icons';
import {faPlusCircle} from '@fortawesome/pro-solid-svg-icons';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import supervisorsAPI from 'BRAND/api/supervisors';

export default {
  name: 'ManageMyAccess',
  components: {
    MainLayout,
    GridCol,
    GridRow,
    Alert,
    Chevron,
    TableComponent,
    TableColumn,
    FontAwesomeIcon,
    OkCancelModal
  },
  created () {
    this.requestData();
  },
  mounted () {
    const code = this.$route.query.code;
    if (code != null) {
      supervisorsAPI.acceptInvitationByCode(this, code).then(() => {
        showAlert(this, this.$t('health.manageAccess.successAcceptInvitation'), 'success');
        this.requestData();
      }).catch(() => {
        showAlert(this, this.$t('health.manageAccess.errorAcceptInvitation'), 'error');
      });
    }
  },
  data () {
    return {
      accessibleUsers: [],
      pendingUsers: [],
      allUsers: [],
      accessToRemove: undefined,
      accessToDecline: undefined,
      icon: {
        trash: faTrashAlt,
        check: faCheck,
        cross: faPlusCircle
      },
      /* Table pagination */
      tableAccessPageSize: 6,
      tableAccessState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },
      tablePendingPageSize: 4,
      tablePendingState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      },

      /* Modals */
      showConfirmationDeleteAccessModal: false,
      deleteAccessModalMessage: this.$t('health.manageAccess.removeAccessMessage'),
      deleteAccessModalTitle: this.$t('health.manageAccess.removeAccessTitle'),
      showConfirmationPendingCancelModal: false,
      deletePendingCancelModalMessage: this.$t('health.manageAccess.removePendingMessage'),
      deletePendingCancelModalTitle: this.$t('health.manageAccess.removePendingTitle'),
      /* UI controls */
      alertVisible: false,
      alertMessage: '',
      alertType: '' // error, warning, success
    };
  },
  computed: {
    getBreadcrumb () {
      return [
        {
          name: this.$t('menu.settings'),
          link: '/settings'
        },
        {
          name: this.$t('health.manageAccess.title'),
          link: '/manage-my-access',
          disabled: true
        }
      ];
    },
    getAccessibleUsers () {
      return this.accessibleUsers;
    },
    getPendingUsers () {
      return this.pendingUsers;
    },
    getAllUsers () {
      return this.allUsers;
    }
  },
  watch: {
    getAccessibleUsers: {
      handler: function () {
        this.$refs.accessibleUsersTable.refresh();
      }
    },
    getPendingUsers: {
      handler: function () {
        this.$refs.pendingUsersTable.refresh();
      }
    }
  },

  methods: {
    fetchAccessiblesData ({page, filter, sort}) {
      const tablePage = pagination.createPage(
        this.getAccessibleUsers,
        this.tableAccessState,
        ['username'],
        this.tableAccessPageSize,
        {page, filter, sort});
      this.tableAccessState = tablePage.state;
      return tablePage.result;
    },
    fetchPendingData ({page, filter, sort}) {
      const tablePage = pagination.createPage(
        this.getPendingUsers,
        this.tablePendingState,
        ['username'],
        this.tablePendingPageSize,
        {page, filter, sort});
      this.tablePendingState = tablePage.state;
      return tablePage.result;
    },
    requestData () {
      supervisorsAPI.getAccessibleAndPendingUsers(this).then(data => {
        this.allUsers = data;
        this.pendingUsers = data.filter(user => user.pending);
        this.accessibleUsers = data.filter(user => !user.pending);
      });
    },
    onRemoveAccess (row) {
      this.accessToRemove = row;
      this.showConfirmationDeleteAccessModal = true;
    },
    onAcceptAccess (row) {
      const id = row.userId;
      supervisorsAPI.acceptInvitation(this, id).then(() => {
        showAlert(this, this.$t('health.manageAccess.successAcceptInvitation'), 'success');
        this.allUsers.find(value => value.userId === id).pending = false;
        this.pendingUsers = this.allUsers.filter(user => user.pending);
        this.accessibleUsers = this.allUsers.filter(user => !user.pending);
      }).catch(() => {
        showAlert(this, this.$t('health.manageAccess.errorAcceptInvitation'), 'error');
      });
    },
    onDeclineAccess (row) {
      this.accessToDecline = row;
      this.showConfirmationPendingCancelModal = true;
    },
    onDeclineAccessConfirm () {
      const id = this.accessToDecline.userId;
      this.accessToDecline = {};
      this.showConfirmationPendingCancelModal = false;
      supervisorsAPI.declineInvitation(this, id).then(() => {
        showAlert(this, this.$t('health.manageAccess.successDeclineInvitation'), 'success');
        this.pendingUsers = this.pendingUsers.filter(value => value.userId !== id);
      }).catch(() => {
        showAlert(this, this.$t('health.manageAccess.errorDeclineInvitation'), 'error');
      });
    },
    onDeclineAccessCancel () {
      this.accessToDecline = {};
      this.showConfirmationPendingCancelModal = false;
    },
    onDeleteAccessConfirm () {
      this.showConfirmationDeleteAccessModal = false;
      const id = this.accessToRemove.userId;
      const username = this.accessToRemove.username;
      this.accessToRemove = {};
      supervisorsAPI.deleteAccess(this, id).then(() => {
        this.accessibleUsers = this.accessibleUsers.filter(value => value.userId !== id);
        showAlert(this, this.$t('health.manageAccess.successAccessDelete', {name: username}), 'success');
      }).catch(() => {
        showAlert(this, this.$t('health.manageAccess.errorAccessDelete', {name: username}), 'error');
      });
    },
    onDeleteAccessCancel () {
      this.showConfirmationDeleteAccessModal = false;
      this.accessToRemove = undefined;
    }
  }
};

const showAlert = (context, message, type) => {
  context.alertVisible = true;
  context.alertMessage = message;
  context.alertType = type;
  context.showAlert = true;
  setTimeout(() => {
    context.alertVisible = false;
  }, 2000);
};
</script>

<style lang="scss" scoped>
@import "src/stylesheets/variables";

.pointer {
 cursor: pointer;
}

.rotated {
  transform: rotate(45deg);
}
</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/ManageMyAccess.vue